/**
 * Deterministic mock fixtures for the unified Appointments landing.
 *
 * Activated by `VITE_FEATURE_APPOINTMENTS_MOCK=true`. Used when:
 *   - LIVE flag is off (most dev / demo today)
 *   - Live Graph call fails (graceful zero-state placeholder)
 *
 * No PII — synthetic names matched to PCB's existing Connor / Margaret /
 * Daniel sample customers used elsewhere in the app.
 */

import type { Appointment } from '../types';

const MIN = 60_000;

interface MockSeed {
  id: string;
  customerName: string;
  subject: string;
  /** Minutes from "now" the appointment starts. Negative = past. */
  startOffsetMin: number;
  durationMin: number;
  channel: Appointment['channel'];
  source: Appointment['source'];
  branchName?: string;
  assignedOfficer?: string;
  assignedOfficerInitials?: string;
  notes?: string;
  arrived?: boolean;
  hasJoinUrl?: boolean;
}

const TODAY_SEEDS: ReadonlyArray<MockSeed> = [
  {
    id: 'mock-today-1',
    customerName: 'Margaret Allen',
    subject: 'Open new account',
    startOffsetMin: 30,
    durationMin: 45,
    channel: 'inBranch',
    source: 'calendar',
    branchName: 'Downtown Rolla',
    assignedOfficer: 'Ella Monroe',
    assignedOfficerInitials: 'EM',
    notes: 'New customer — bringing both license and proof of address.',
  },
  {
    id: 'mock-today-2',
    customerName: 'Connor Sample',
    subject: 'CD rate review',
    startOffsetMin: 90,
    durationMin: 30,
    channel: 'teams',
    source: 'calendar',
    branchName: 'Teams meeting',
    assignedOfficer: 'Ella Monroe',
    assignedOfficerInitials: 'EM',
    hasJoinUrl: true,
    notes: '$25k maturity — wants to compare 12-mo and 24-mo terms.',
  },
  {
    id: 'mock-today-3',
    customerName: 'Daniel Park',
    subject: 'Address update',
    startOffsetMin: 180,
    durationMin: 20,
    channel: 'phone',
    source: 'calendar',
    branchName: 'Phone',
    assignedOfficer: 'Ella Monroe',
    assignedOfficerInitials: 'EM',
  },
  {
    id: 'mock-today-4',
    customerName: 'Sarah Linwood',
    subject: 'Loan inquiry',
    startOffsetMin: 240,
    durationMin: 30,
    channel: 'inBranch',
    source: 'bookings',
    branchName: 'Downtown Rolla',
    assignedOfficer: 'Lori Henderson',
    assignedOfficerInitials: 'LH',
    notes: 'Booked via public Bookings page.',
  },
];

const UPCOMING_SEEDS: ReadonlyArray<MockSeed> = [
  {
    id: 'mock-up-1',
    customerName: 'Maria Chen',
    subject: 'IRA opening',
    startOffsetMin: 24 * 60 + 60,
    durationMin: 60,
    channel: 'inBranch',
    source: 'calendar',
    branchName: 'Forum Branch',
    assignedOfficer: 'Ella Monroe',
    assignedOfficerInitials: 'EM',
  },
  {
    id: 'mock-up-2',
    customerName: 'Robert Hayes',
    subject: 'SDB close-out',
    startOffsetMin: 2 * 24 * 60,
    durationMin: 30,
    channel: 'inBranch',
    source: 'calendar',
    branchName: 'Downtown Rolla',
    assignedOfficer: 'Ella Monroe',
    assignedOfficerInitials: 'EM',
  },
  {
    id: 'mock-up-3',
    customerName: 'Jenna Kowalski',
    subject: 'Business banking review',
    startOffsetMin: 3 * 24 * 60 + 30,
    durationMin: 45,
    channel: 'teams',
    source: 'bookings',
    branchName: 'Teams meeting',
    assignedOfficer: 'Marcus Webb',
    assignedOfficerInitials: 'MW',
    hasJoinUrl: true,
  },
];

const BOOKINGS_SEEDS: ReadonlyArray<MockSeed> = [
  {
    id: 'mock-book-1',
    customerName: 'Sarah Linwood',
    subject: 'Loan inquiry',
    startOffsetMin: 240,
    durationMin: 30,
    channel: 'inBranch',
    source: 'bookings',
    assignedOfficer: 'Lori Henderson',
    assignedOfficerInitials: 'LH',
    notes: 'Booked via public Bookings page.',
  },
  {
    id: 'mock-book-2',
    customerName: 'Trevor Goldman',
    subject: 'Account review',
    startOffsetMin: 24 * 60,
    durationMin: 30,
    channel: 'bookings',
    source: 'bookings',
    assignedOfficer: '(unassigned)',
    assignedOfficerInitials: '?',
  },
  {
    id: 'mock-book-3',
    customerName: 'Amelia Patel',
    subject: 'New checking account',
    startOffsetMin: 2 * 24 * 60 + 90,
    durationMin: 45,
    channel: 'teams',
    source: 'bookings',
    assignedOfficer: '(unassigned)',
    hasJoinUrl: true,
  },
];

function seedToAppointment(s: MockSeed, branchCode?: string, branchName?: string): Appointment {
  const start = new Date(Date.now() + s.startOffsetMin * MIN);
  const end = new Date(start.getTime() + s.durationMin * MIN);
  const out: Appointment = {
    id: s.id,
    customerName: s.customerName,
    subject: s.subject,
    startUtc: start.toISOString(),
    endUtc: end.toISOString(),
    timeZone: 'America/Chicago',
    channel: s.channel,
    source: s.source,
  };
  if (branchCode) out.branchCode = branchCode;
  const labelBranch = s.branchName ?? branchName;
  if (labelBranch) out.branchName = labelBranch;
  if (s.assignedOfficer) out.assignedOfficer = s.assignedOfficer;
  if (s.assignedOfficerInitials) out.assignedOfficerInitials = s.assignedOfficerInitials;
  if (s.notes) out.notes = s.notes;
  if (s.arrived) out.arrived = true;
  if (s.hasJoinUrl) out.joinWebUrl = 'https://teams.microsoft.com/l/meetup-join/mock';
  return out;
}

export function mockAppointmentsForToday(branchCode?: string): ReadonlyArray<Appointment> {
  return TODAY_SEEDS.map((s) => seedToAppointment(s, branchCode));
}

export function mockAppointmentsForUpcoming(branchCode?: string): ReadonlyArray<Appointment> {
  return UPCOMING_SEEDS.map((s) => seedToAppointment(s, branchCode));
}

export function mockBookingsForBranch(branchCode: string, branchName?: string): ReadonlyArray<Appointment> {
  return BOOKINGS_SEEDS.map((s) => seedToAppointment(s, branchCode, branchName));
}
